// Per-company session settings. Triggered by ⚙ in topbar or the
// Settings item in the sidebar row 3-dot menu.
//
// Sections:
//   - Info (company name + apiBase host)
//   - Rotate key (paste curl/JSON → store.rotateKey)
//   - Delete session (destructive — wipes data, never the website)

import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, CheckCircle, Trash2, X, Key, Upload, Globe } from "react-feather";
import { useSession } from "../store/session";
import { parseCurlOrJson } from "../lib/curl-parser";
import { useReviewSheet } from "../store/reviewSheet";

interface Props {
  companyId: string;
}

function maskBearer(t: string): string {
  if (t.length <= 10) return "•".repeat(t.length);
  return `${t.slice(0, 4)}…${t.slice(-4)} (${t.length} chars)`;
}

export default function SessionSettingsModal({ companyId }: Props) {
  const closeSettings = useSession((s) => s.closeSettings);
  const company = useSession((s) =>
    s.companies.find((c) => c.id === companyId),
  );
  const rotateKey = useSession((s) => s.rotateKey);
  const changeDomain = useSession((s) => s.changeDomain);
  const deleteCompany = useSession((s) => s.deleteCompany);
  const setAutomationFreeze = useSession((s) => s.setAutomationFreeze);
  const [reviewedHold, setReviewedHold] = useState(false);
  const [liftingHold, setLiftingHold] = useState(false);

  const [paste, setPaste] = useState("");
  const [rotating, setRotating] = useState(false);
  const [rotateError, setRotateError] = useState<string | null>(null);
  const [rotateSuccess, setRotateSuccess] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const [newDomain, setNewDomain] = useState("");
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState<string | null>(null);
  const [moveSuccess, setMoveSuccess] = useState(false);

  // Mirrors AddCompanyModal.onFilePick — same .json/.txt filter, same 1MB
  // cap, same flow: read file → drop the text into the paste box so the
  // existing parse-preview path lights up exactly as if the user typed it.
  async function onFilePick(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.currentTarget.files?.[0];
    e.currentTarget.value = "";
    if (!f) return;
    if (f.size > 1024 * 1024) {
      setRotateError("File too large (max 1MB).");
      return;
    }
    try {
      const text = await f.text();
      setPaste(text);
      setRotateError(null);
      setRotateSuccess(false);
    } catch (err: any) {
      setRotateError(`Couldn't read file: ${err.message}`);
    }
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        if (confirmingDelete) setConfirmingDelete(false);
        else closeSettings();
      }
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [closeSettings, confirmingDelete]);

  const parsed = useMemo(() => (paste.trim() ? parseCurlOrJson(paste) : null), [paste]);
  const parseError = parsed && !parsed.ok ? parsed.error : null;
  const parseOk = !!(parsed && parsed.ok);

  if (!company) {
    // Company was deleted underneath us — close.
    closeSettings();
    return null;
  }

  async function doRotate() {
    if (!parseOk || rotating) return;
    setRotating(true);
    setRotateError(null);
    setRotateSuccess(false);
    try {
      await rotateKey(companyId, paste);
      setRotateSuccess(true);
      setPaste("");
    } catch (err: any) {
      setRotateError(err.message ?? "Rotate failed.");
    } finally {
      setRotating(false);
    }
  }

  async function doMove() {
    const target = newDomain.trim();
    if (!target || moving) return;
    setMoving(true);
    setMoveError(null);
    setMoveSuccess(false);
    try {
      await changeDomain(companyId, target);
      setMoveSuccess(true);
      setNewDomain("");
    } catch (err: any) {
      setMoveError(err.message ?? "Move failed.");
    } finally {
      setMoving(false);
    }
  }

  async function doDelete() {
    setDeleting(true);
    try {
      await deleteCompany(companyId);
      closeSettings();
    } catch (err: any) {
      setRotateError(err.message ?? "Delete failed.");
      setDeleting(false);
      setConfirmingDelete(false);
    }
  }

  return (
    <div
      className="historyConfirm__backdrop"
      onClick={closeSettings}
      role="dialog"
      aria-modal="true"
      aria-labelledby="settings-title"
    >
      <div
        className="historyConfirm__modal historyConfirm__modal--wide"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          className="modal__close"
          onClick={closeSettings}
          aria-label="Close"
        >
          <X width={16} height={16} />
        </button>

        <h3 className="historyConfirm__title" id="settings-title">
          {company.name}
        </h3>
        {company.apiBase && (
          <p className="historyConfirm__body modalField__hint">
            <code>{new URL(company.apiBase).host}</code>
          </p>
        )}

        {company.automationFrozen && (
          <div className="modalSection" role="status">
            <div className="modalSection__header"><AlertTriangle width={14} height={14} /><span>Review safety hold</span></div>
            <p className="modalSection__hint">Older sample-to-client conversions could add this hold automatically. New conversions do not. Auto-approve is a saved preference; this separate hold blocks new portal work and schedules. Nothing pending is lost.</p>
            <div className="modalSection__actions">
              <button type="button" className="btn" onClick={() => { closeSettings(); useReviewSheet.getState().open("chat"); }}>Review pending work</button>
              <button type="button" className="btn" onClick={() => { closeSettings(); useSession.getState().openScheduled(); }}>Review schedules</button>
            </div>
            <label className="modalSection__hint" style={{ display: "flex", alignItems: "flex-start", gap: 8, margin: "16px 0" }}>
              <input type="checkbox" checked={reviewedHold} onChange={e => setReviewedHold(e.target.checked)} disabled={liftingHold} />
              <span>I reviewed pending work and schedules. Allow them to continue under the current settings.</span>
            </label>
            <button type="button" className="btn btn--primary" disabled={!reviewedHold || liftingHold} onClick={async () => {
              setLiftingHold(true);
              try { await setAutomationFreeze(companyId, false, reviewedHold); }
              finally { setLiftingHold(false); }
            }}>{liftingHold ? "Lifting hold…" : "Lift safety hold"}</button>
            <p className="modalSection__hint">An existing Stop pause stays paused. Lifting this hold does not turn Auto-approve on if it is off.</p>
          </div>
        )}

        {/* --- Change domain (keep key) --- */}
        <div className="modalSection">
          <div className="modalSection__header">
            <Globe width={14} height={14} />
            <span>Change Domain</span>
          </div>
          <p className="modalSection__hint">
            Moved to a new domain, same key? Paste the new URL — the key stays.
            (Cloaked / rotated the key too? Use <strong>Rotate Key</strong>.)
          </p>
          <input
            type="text"
            className="modalField__input"
            placeholder="https://newdomain.com"
            value={newDomain}
            onChange={(e) => {
              setNewDomain(e.currentTarget.value);
              setMoveSuccess(false);
              setMoveError(null);
            }}
            disabled={moving}
            spellCheck={false}
            autoComplete="off"
            autoCapitalize="off"
            onKeyDown={(e) => {
              if (e.key === "Enter") doMove();
            }}
          />
          {moveError && (
            <div className="modalPreview modalPreview--err">
              <AlertTriangle width={14} height={14} />
              <span>{moveError}</span>
            </div>
          )}
          {moveSuccess && (
            <div className="modalPreview modalPreview--ok">
              <CheckCircle width={14} height={14} />
              <span>Re-pointed to the new domain — key kept.</span>
            </div>
          )}
          <div className="modalSection__footer">
            <button
              type="button"
              className="historyConfirm__btn historyConfirm__btn--primary"
              onClick={doMove}
              disabled={!newDomain.trim() || moving}
            >
              {moving ? (
                <>
                  <span className="spinner" />
                  <span>Moving…</span>
                </>
              ) : (
                <span>Move Site</span>
              )}
            </button>
          </div>
        </div>

        {/* --- Rotate key --- */}
        <div className="modalSection">
          <div className="modalSection__header">
            <Key width={14} height={14} />
            <span>Rotate Key</span>
          </div>
          <p className="modalSection__hint">
            Paste a new curl or JSON to replace this key.
          </p>
          {/* Upload chip mirrors AddCompanyModal so the rotate flow accepts
              the same Reputifly Agent profile .json downloads — no need to
              copy-paste from a text editor on mobile. */}
          <div className="modalField__labelRow">
            <span className="modalField__hint" style={{ flex: 1 }} />
            <button
              type="button"
              className="modalField__uploadBtn"
              onClick={() => fileRef.current?.click()}
              disabled={rotating}
            >
              <Upload width={12} height={12} />
              <span>Upload .json</span>
            </button>
            <input
              ref={fileRef}
              type="file"
              accept=".json,.txt,application/json,text/plain"
              style={{ display: "none" }}
              onChange={onFilePick}
            />
          </div>
          <textarea
            className="modalField__textarea"
            placeholder="Paste new curl or JSON here…"
            value={paste}
            onChange={(e) => {
              setPaste(e.currentTarget.value);
              setRotateSuccess(false);
              setRotateError(null);
            }}
            rows={5}
            disabled={rotating}
            spellCheck={false}
            autoComplete="off"
          />
          {paste.trim() && parsed && (
            parsed.ok ? (
              <div className="modalPreview modalPreview--ok">
                <CheckCircle width={14} height={14} />
                <div className="modalPreview__lines">
                  <code className="modalPreview__line">{parsed.value!.apiBase}</code>
                  <code className="modalPreview__line modalPreview__line--muted">
                    Bearer {maskBearer(parsed.value!.bearer)}
                  </code>
                </div>
              </div>
            ) : (
              <div className="modalPreview modalPreview--err">
                <AlertTriangle width={14} height={14} />
                <span>{parseError}</span>
              </div>
            )
          )}
          {rotateError && (
            <div className="modalPreview modalPreview--err">
              <AlertTriangle width={14} height={14} />
              <span>{rotateError}</span>
            </div>
          )}
          {rotateSuccess && (
            <div className="modalPreview modalPreview--ok">
              <CheckCircle width={14} height={14} />
              <span>Key rotated.</span>
            </div>
          )}
          <div className="modalSection__footer">
            <button
              type="button"
              className="historyConfirm__btn historyConfirm__btn--primary"
              onClick={doRotate}
              disabled={!parseOk || rotating}
            >
              {rotating ? (
                <>
                  <span className="spinner" />
                  <span>Rotating…</span>
                </>
              ) : (
                <span>Rotate Key</span>
              )}
            </button>
          </div>
        </div>

        {/* --- Delete session --- */}
        <div className="modalSection modalSection--danger">
          <div className="modalSection__header">
            <Trash2 width={14} height={14} />
            <span>Delete Session</span>
          </div>
          <p className="modalSection__hint">
            Wipes this company's chat, notes &amp; registry entry.
            <strong> The website is untouched.</strong>
          </p>

          {!confirmingDelete ? (
            <div className="modalSection__footer">
              <button
                type="button"
                className="historyConfirm__btn historyConfirm__btn--danger"
                onClick={() => setConfirmingDelete(true)}
                disabled={deleting}
              >
                Delete Session
              </button>
            </div>
          ) : (
            <div className="modalSection__confirmRow">
              <span className="modalSection__confirmLabel">
                Really delete "{company.name}"?
              </span>
              <button
                type="button"
                className="historyConfirm__btn historyConfirm__btn--cancel"
                onClick={() => setConfirmingDelete(false)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="historyConfirm__btn historyConfirm__btn--danger"
                onClick={doDelete}
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <span className="spinner" />
                    <span>Deleting…</span>
                  </>
                ) : (
                  <span>Yes, delete</span>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
